import React, { useEffect, useState } from "react";
import client, { formatZAR } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, Trash2 } from "lucide-react";
import { ModuleHeader, StatusPill, EmptyState } from "./AdminUI";
import { ORDER_STATUSES, ALL_ORDER_STATUSES, ENQUIRY_STATUSES, PAYMENT_LABELS, statusLabel, downloadCsv } from "./adminShared";

function OrderCard({ o, onStatus }) {
    return (
        <div data-testid={`admin-order-${o.order_number}`} className="border border-brand-line p-5 rounded-sm bg-brand-charcoal">
            <div className="flex flex-wrap justify-between items-start gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <p className="font-display text-xl text-brand-gold">{o.order_number}</p>
                        <StatusPill status={o.status} label={statusLabel(ALL_ORDER_STATUSES, o.status)} />
                    </div>
                    <p className="text-sm mt-1">{o.customer?.name} · <a href={`tel:${o.customer?.phone}`} className="hover:text-brand-gold">{o.customer?.phone}</a></p>
                    {o.customer?.email && <p className="text-xs text-brand-mute">{o.customer.email}</p>}
                    <p className="text-[11px] text-brand-mute mt-1">{new Date(o.created_at).toLocaleString("en-ZA")}</p>
                </div>
                <div className="text-right">
                    <p className="text-lg">{formatZAR(o.total)}</p>
                    <p className="text-xs text-brand-mute mt-1">{PAYMENT_LABELS[o.payment_method] || o.payment_method} · {o.payment_status}</p>
                </div>
            </div>
            <ul className="mt-3 text-sm text-brand-cream/85 space-y-1">
                {o.lines?.map((l, i) => (
                    <li key={i}>
                        {l.quantity} × {l.name}
                        {(l.modifiers || []).length > 0 && <span className="text-brand-mute"> — {l.modifiers.map((m) => m.name).join(", ")}</span>}
                        {" "}— {formatZAR(l.line_total)}
                    </li>
                ))}
            </ul>
            {o.customer?.notes && <p className="mt-2 text-xs italic text-brand-cream/70">Notes: {o.customer.notes}</p>}
            <div className="mt-4 flex gap-2 flex-wrap">
                {ORDER_STATUSES.map((s) => (
                    <button
                        key={s.value}
                        data-testid={`order-${o.order_number}-status-${s.value}`}
                        onClick={() => onStatus(o.id, s.value)}
                        className={`text-xs px-3 py-1.5 border rounded-sm transition-colors ${
                            o.status === s.value ? "bg-brand-olive border-brand-olive text-brand-cream" : "border-brand-line text-brand-cream/70 hover:border-brand-gold"
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function OrdersPanel({ onChange }) {
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState("open");
    const load = () => client.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, []);

    const setStatus = async (id, status) => {
        await client.patch(`/admin/orders/${id}`, { status });
        toast.success(`Marked ${statusLabel(ORDER_STATUSES, status)}`);
        load();
        onChange();
    };

    const match = (o) => filter === "all" || (filter === "open" ? ["new", "in_progress"].includes(o.status) : o.status === filter);
    const shown = orders.filter(match);
    const chips = [{ value: "open", label: "Open" }, ...ALL_ORDER_STATUSES, { value: "all", label: "All" }];
    const countFor = (v) => orders.filter((o) => (v === "all" ? true : v === "open" ? ["new", "in_progress"].includes(o.status) : o.status === v)).length;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                    {chips.map((c) => (
                        <button key={c.value} data-testid={`orders-filter-${c.value}`} onClick={() => setFilter(c.value)}
                            className={`text-xs px-3 py-1.5 border rounded-sm transition-colors ${filter === c.value ? "border-brand-gold text-brand-gold" : "border-brand-line text-brand-cream/70 hover:text-brand-cream"}`}>
                            {c.label} <span className="text-brand-mute">{countFor(c.value)}</span>
                        </button>
                    ))}
                </div>
                <button data-testid="orders-export-csv" onClick={() => downloadCsv("/admin/export/orders.csv", "umami-orders.csv")} className="btn-ghost text-xs inline-flex items-center gap-2"><Download size={13} /> Export CSV</button>
            </div>
            <p className="text-xs text-brand-mute">Refreshes every 15 seconds. WhatsApp orders are saved here before the customer is sent to WhatsApp.</p>
            {shown.length === 0 ? <EmptyState>No orders in this view.</EmptyState> : shown.map((o) => <OrderCard key={o.id} o={o} onStatus={setStatus} />)}
        </div>
    );
}

function EnquiriesPanel({ onChange }) {
    const [list, setList] = useState([]);
    const load = () => client.get("/admin/messages").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        await client.patch(`/admin/messages/${id}`, { status });
        toast.success("Enquiry updated");
        load();
        onChange();
    };
    const remove = async (e) => {
        if (!window.confirm(`Delete enquiry from ${e.name}?`)) return;
        await client.delete(`/admin/messages/${e.id}`);
        load();
        onChange();
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button data-testid="enquiries-export-csv" onClick={() => downloadCsv("/admin/export/enquiries.csv", "umami-enquiries.csv")} className="btn-ghost text-xs inline-flex items-center gap-2"><Download size={13} /> Export CSV</button>
            </div>
            {list.length === 0 ? <EmptyState>No enquiries yet.</EmptyState> : list.map((e) => (
                <div key={e.id} data-testid={`enquiry-${e.id}`} className="border border-brand-line p-5 rounded-sm bg-brand-charcoal">
                    <div className="flex flex-wrap justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-3">
                                <p className="text-brand-cream">{e.name}</p>
                                <StatusPill status={e.status} label={statusLabel(ENQUIRY_STATUSES, e.status)} />
                            </div>
                            <p className="text-xs text-brand-mute mt-1">
                                <a href={`mailto:${e.email}`} className="hover:text-brand-gold">{e.email}</a>{e.phone ? ` · ${e.phone}` : ""} · {new Date(e.created_at).toLocaleString("en-ZA")}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <select data-testid={`enquiry-status-${e.id}`} value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} className="bg-brand-ink border border-brand-line text-xs px-2 py-1.5 rounded-sm">
                                {ENQUIRY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                            <button data-testid={`enquiry-delete-${e.id}`} onClick={() => remove(e)} className="p-1.5 border border-brand-line hover:border-destructive hover:text-destructive rounded-sm"><Trash2 size={13} /></button>
                        </div>
                    </div>
                    <p className="text-sm text-brand-cream/85 mt-3 whitespace-pre-line">{e.message}</p>
                </div>
            ))}
        </div>
    );
}

export default function OrdersModule({ onCountsChange }) {
    return (
        <div data-testid="admin-orders-module">
            <ModuleHeader eyebrow="Inbox" title="Orders & Enquiries" subtitle="Move each order through New → In Progress → Fulfilled → Done." />
            <Tabs defaultValue="orders" onValueChange={onCountsChange}>
                <TabsList className="bg-brand-charcoal border border-brand-line rounded-sm">
                    <TabsTrigger value="orders" data-testid="inbox-tab-orders">Orders</TabsTrigger>
                    <TabsTrigger value="enquiries" data-testid="inbox-tab-enquiries">Enquiries</TabsTrigger>
                </TabsList>
                <TabsContent value="orders" className="mt-6"><OrdersPanel onChange={onCountsChange} /></TabsContent>
                <TabsContent value="enquiries" className="mt-6"><EnquiriesPanel onChange={onCountsChange} /></TabsContent>
            </Tabs>
        </div>
    );
}
